import { PersonStanding, X, Contrast, AArrowUp, Eye, Baseline, Settings2 } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export const Accessibility = () => {
  const [open, setOpen] = useState(false);
  const accessibilityDialogRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const triggerRef = useRef(null);
  const { t } = useTranslation();

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Global keyboard shortcut: Alt + A to toggle accessibility menu
    if (e.altKey && e.key === 'a' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleAccessibilityButtonClick();
    }

    if (!open) return;

    const focusableElements = accessibilityDialogRef.current?.querySelectorAll(
      'button, [href], [tabindex]:not([tabindex="-1"])'
    );
    
    if (!focusableElements || focusableElements.length === 0) return;
    
    const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
        
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
          focusableElements[prevIndex].focus();
        } else {
          const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
          focusableElements[nextIndex].focus();
        }
        break;
        
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        focusableElements[nextIndex].focus();
        break;
        
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        focusableElements[prevIndex].focus();
        break;
        
      case 'Home':
        e.preventDefault();
        focusableElements[0].focus();
        break;
        
      case 'End':
        e.preventDefault();
        focusableElements[focusableElements.length - 1].focus();
        break;
    }
  };

  const openMenu = () => {
    setOpen(true);
    // Focus first focusable element after menu opens
    setTimeout(() => {
      firstFocusableRef.current?.focus();
    }, 100);
  };

  const closeMenu = () => {
    setOpen(false);
    // Return focus to trigger button
    triggerRef.current?.focus();
  };

  const handleAccessibilityButtonClick = () => {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const handleAccessibilityButtonClickOnEnter = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleAccessibilityButtonClick();
    }
  };


  // Utility to persist and restore accessibility settings
  const setSession = (key, value) => {
    try {
      sessionStorage.setItem(key, value ? "1" : "0");
    } catch {}
  };
  const getSession = (key) => {
    try {
      return sessionStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  };

  const increaseContrast = () => {
    const enabled = !document.body.classList.contains("high-contrast");
    document.body.classList.toggle("high-contrast");
    setSession("accessibility_high_contrast", enabled);
  };

  const increaseFontSize = () => {
    const html = document.querySelector("html");
    const enabled = !html.classList.contains("large-font");
    html.classList.toggle("large-font");
    setSession("accessibility_large_font", enabled);
  };

  const increaseLetterSpacing = () => {
    const html = document.querySelector("html");
    const enabled = !html.classList.contains("increased-spacing");
    html.classList.toggle("increased-spacing");
    setSession("accessibility_increased_spacing", enabled);
  };

  // Restore settings on mount
  useEffect(() => {
    if (getSession("accessibility_high_contrast")) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
    const html = document.querySelector("html");
    if (getSession("accessibility_large_font")) {
      html.classList.add("large-font");
    } else {
      html.classList.remove("large-font");
    }
    if (getSession("accessibility_increased_spacing")) {
      html.classList.add("increased-spacing");
    } else {
      html.classList.remove("increased-spacing");
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accessibilityDialogRef.current &&
        !accessibilityDialogRef.current.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        closeMenu();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <div 
        ref={triggerRef}
        className={`accessibility-button ${open ? 'open' : ''}`} 
        role="button" 
        aria-label="Accessibility options - press Alt+A to toggle" 
        aria-expanded={open}
        aria-haspopup="menu"
        tabIndex="0" 
        onClick={handleAccessibilityButtonClick} 
        onKeyDown={handleAccessibilityButtonClickOnEnter}
        aria-describedby="accessibility-tooltip"
      >
        {open ? (
          <X size={26} className="icon" aria-hidden="true" />
        ) : (
          <PersonStanding size={26} className="icon" aria-hidden="true" />
        )}
        <span id="accessibility-tooltip" className="sr-only">
          Naciśnij Alt+A aby otworzyć menu dostępności
        </span>
      </div>
      
      <div 
        className={`accessibility-menu ${open ? 'open' : ''}`} 
        role="menu" 
        ref={accessibilityDialogRef}
        aria-labelledby="accessibility-menu-title"
        aria-describedby="keyboard-instructions"
      >
        <h2 id="accessibility-menu-title">{t("accessibility.option")}</h2>
        <div id="keyboard-instructions" className="sr-only">
          Użyj strzałek do nawigacji, Enter aby aktywować, Escape aby zamknąć
        </div>
        <div className="accessibility-menu__options" role="menubar">
          <button 
            ref={firstFocusableRef}
            className="accessibility-menu__option" 
            aria-label="Zwiększ kontrast" 
            onClick={increaseContrast}
            role="menuitem"
            tabIndex={open ? 0 : -1}
          >
            <Contrast size={26} className="option-icon" aria-hidden="true" />
            <span>{t("accessibility.kontrast")}</span>
          </button>
          
          <button 
            className="accessibility-menu__option" 
            aria-label="Zwiększ rozmiar czcionki" 
            onClick={increaseFontSize}
            role="menuitem"
            tabIndex={open ? 0 : -1}
          >
            <AArrowUp size={26} className="option-icon" aria-hidden="true" />
            <span>{t("accessibility.text")}</span>
          </button>
          
          <button 
            className="accessibility-menu__option" 
            aria-label="Zwiększ odstępy między literami" 
            onClick={increaseLetterSpacing}
            role="menuitem"
            tabIndex={open ? 0 : -1}
          >
            <Baseline size={26} className="option-icon" aria-hidden="true" />
            <span>{t("accessibility.letters")}</span>
          </button>
          
        </div>
      </div>
      <style jsx>
        {`.accessibility-button {
  position: fixed;
  bottom: 10px;
  left: 10px;
  width: 50px;
  height: 50px;
  backdrop-filter: blur(7px);
  background-color: rgba(44, 133, 192, 0.6862745098);
  border: 2px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
}
.accessibility-button:hover, .accessibility-button:focus {
  transform: scale(1.1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}
.accessibility-button.open {
  background-color: #fff;
  transform: scale(1.1);
}
.accessibility-button:focus-visible {
  outline: 2px solid #0065C3;
  outline-offset: 2px;
}

.accessibility-menu {
  display: none;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}
.accessibility-menu.open {
  display: block;
  opacity: 1;
  transform: translateY(0);
  position: fixed;
  bottom: 70px;
  left: 10px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #fff;
  border: 2px solid black;
  border-radius: 8px;
  z-index: 1001;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  padding: 20px;
}
.accessibility-menu h2 {
  margin: 0 0 20px;
  font-size: 1.2em;
  color: black;
  font-weight: bold;
}
.accessibility-menu__options {
  display: grid;
  gap: 10px;
}
.accessibility-menu__option {
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  border: 2px solid black;
  padding: 0;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s ease;
  border-radius: 4px;
  overflow: hidden;
}
.accessibility-menu__option:hover, .accessibility-menu__option:focus {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #f5f5f5;
  transform: translateY(-1px);
}
.accessibility-menu__option:focus-visible {
  outline: 2px solid #0065C3;
  outline-offset: 2px;
}
.accessibility-menu__option:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.accessibility-menu__option .option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  background-color: #43a1e0;
  border-right: 2px solid black;
}
.accessibility-menu__option span {
  padding: 0 12px;
  text-align: left;
}

.high-contrast .accessibility-button {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu h2 {
  color: #fff;
}
.high-contrast .accessibility-menu__option {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu__option:hover, .high-contrast .accessibility-menu__option:focus {
  background-color: #333;
}
.high-contrast .accessibility-menu__option .option-icon {
  background-color: #333;
  border-color: #fff;
}

@media (prefers-reduced-motion: reduce) {
  .accessibility-button,
  .accessibility-menu,
  .accessibility-menu__option {
    transition: none !important;
  }
}
.reduce-motion .accessibility-button,
.reduce-motion .accessibility-menu,
.reduce-motion .accessibility-menu__option {
  transition: none !important;
}

@media (max-width: 400px) {
  .accessibility-menu {
    width: calc(100vw - 20px);
    left: 10px;
    right: 10px;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}`}
      </style>
    </>
  )
}
